import { useCallback, useEffect, useRef, useState } from 'react';
import { PlusIcon, UploadIcon } from '@/components/icons';
import { isCsvFile, CSV_FILE_ACCEPT } from '@/utils/csvAttachment';

interface ComposerAttachMenuProps {
  onFileSelect: (file: File) => void;
  onError: (message: string) => void;
}

export function ComposerAttachMenu({ onFileSelect, onError }: ComposerAttachMenuProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const openFilePicker = useCallback(() => {
    setOpen(false);
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      if (!isCsvFile(file)) {
        onError('Only .csv files are supported.');
        return;
      }
      onFileSelect(file);
    },
    [onError, onFileSelect]
  );

  return (
    <div className="chat-input__attach-wrap" ref={wrapRef}>
      <input
        ref={fileInputRef}
        type="file"
        accept={CSV_FILE_ACCEPT}
        className="chat-input__attach-input"
        tabIndex={-1}
        aria-hidden
        onChange={handleFileChange}
      />
      <button
        type="button"
        className={`chat-input__attach-btn${open ? ' chat-input__attach-btn--open' : ''}`}
        aria-label="Add attachment"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((s) => !s)}
      >
        <PlusIcon />
      </button>
      {open ? (
        <div className="chat-input__attach-menu" role="menu">
          <button
            type="button"
            className="chat-input__attach-option"
            role="menuitem"
            onClick={openFilePicker}
          >
            <UploadIcon />
            <span>Upload files</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}
